import DiffMatchPatch from "diff-match-patch";

import { CONTENT_STATUS_BADGES } from "../../../theme/badgeTones.js";

import { inlineImageCss } from "../../../utils/inlineImageStyle.js";

import { isPublishablePlatform } from "./platformMeta.jsx";

export const WORKFLOW_STEPS = ["generation", "review", "scheduling"];

/** Minimal markdown → HTML (escaped) used for previews and diffs. */
export const markdownToHtmlSimple = (text) => {
  if (!text) return "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // Before the emphasis rules, with `_` and `*` as entities so they cannot pair across URLs.
    .replace(/!\[([^\]\n]*)\]\((https?:\/\/[^\s)"]+)\)/g, (_, alt, url) =>
      `<img src="${url.replace(/_/g, "&#95;").replace(/\*/g, "&#42;")}" alt="${alt.replace(/[_*"]/g, "")}" style="${inlineImageCss(320)}margin:4px 0;" />`
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    .replace(/(?<!_)_([^_]+)_(?!_)/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Bare URLs (not the ones already turned into links above) become links too.
    .replace(
      /(^|[\s(])((?:https?:\/\/|www\.)[^\s<]*[^\s<.,;:!?)"'])/g,
      (_, lead, url) => `${lead}<a href="${url.startsWith("www.") ? `https://${url}` : url}" target="_blank" rel="noopener">${url}</a>`
    )
    .replace(/^#{1,6}\s+(.+)$/gm, "<strong>$1</strong>")
    .replace(/(^|\s)(#[\p{L}\p{N}_]+)/gu, '$1<span class="mk-hashtag">$2</span>')
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
};

// ─── Texto tal cual lo publica cada red (espejo de `plain_text`/`network_text` del backend) ───

const STYLED_PLATFORMS = ["linkedin", "instagram", "facebook", "tiktok"];
// El texto no enlaza en estas redes: del enlace solo queda la etiqueta.
const UNLINKED_PLATFORMS = ["instagram", "tiktok"];
const UNICODE_STYLES = { bold: [0x1d5d4, 0x1d5ee, 0x1d7ec], italic: [0x1d608, 0x1d622, null] };

const unicodeStyle = (text, style) => {
  const [upper, lower, digit] = UNICODE_STYLES[style];
  return Array.from(text.normalize("NFD"), (char) => {
    if (char >= "A" && char <= "Z") return String.fromCodePoint(upper + char.charCodeAt(0) - 65);
    if (char >= "a" && char <= "z") return String.fromCodePoint(lower + char.charCodeAt(0) - 97);
    if (digit && char >= "0" && char <= "9") return String.fromCodePoint(digit + char.charCodeAt(0) - 48);
    return char;
  })
    .join("")
    .normalize("NFC");
};

const markdownLink = (label, url, withUrl) => {
  const trimmed = label.trim();
  const bare = url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/+$/, "");
  if ([url, bare, `www.${bare}`].includes(trimmed.replace(/\/+$/, ""))) return url;
  return withUrl ? `${trimmed} (${url})` : trimmed;
};

export const networkText = (platform, markdown) => {
  const styled = STYLED_PLATFORMS.includes(platform);
  const links = !UNLINKED_PLATFORMS.includes(platform);
  return String(markdown || "")
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, label, url) => markdownLink(label, url, links))
    .replace(/(\*\*|__)(?=\S)(.+?)(?<=\S)\1/g, (_, __, inner) => (styled ? unicodeStyle(inner, "bold") : inner))
    .replace(/(?<![\p{L}\p{N}_\\*])([_*])(?=\S)(.+?)(?<=[^\s\\])\1(?![\p{L}\p{N}_*])/gu, (_, __, inner) =>
      styled ? unicodeStyle(inner, "italic") : inner
    )
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, "$1");
};

/** HTML of `networkText` for the phone preview: links only where the network makes them. */
export const networkTextHtml = (platform, markdown) => {
  let html = networkText(platform, markdown)
    .replace(/\r\n/g, "\n")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (!UNLINKED_PLATFORMS.includes(platform)) {
    html = html.replace(
      /(^|[\s(])((?:https?:\/\/|www\.)[^\s<]*[^\s<.,;:!?)"'])/g,
      (_, lead, url) => `${lead}<a href="${url.startsWith("www.") ? `https://${url}` : url}" target="_blank" rel="noopener">${url}</a>`
    );
  }
  return html
    .replace(/(^|\s)(#[\p{L}\p{N}_]+)/gu, '$1<span class="mk-hashtag">$2</span>')
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
};

/** Starting height of a network's text editor (+ 42px toolbar); a blog article needs room to read. */
export const textEditorHeight = (platform) => (platform === "blog" ? "75vh" : "280px");

/** Plain text preview (no markdown marks), for clamped card texts. */
export const markdownToPlain = (text) =>
  String(text || "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>#]+/g, "")
    .replace(/\n{2,}/g, "\n")
    .trim();

/** Headings of a long text (blog), shown as the article outline. */
export const extractHeadings = (text, max = 5) =>
  String(text || "")
    .split("\n")
    .map((line) => line.match(/^#{2,4}\s+(.+)$/)?.[1]?.replace(/[*_]/g, "").trim())
    .filter(Boolean)
    .slice(0, max);

/** Inline diff between two texts: removed (red, struck) and added (green). */
export const buildDiffHtml = (previousText, currentText, palette) => {
  const dmp = new DiffMatchPatch();
  dmp.Diff_Timeout = 1;
  const diffs = dmp.diff_main(previousText || "", currentText || "");
  dmp.diff_cleanupSemantic(diffs);
  const changes = diffs.filter(([type]) => type !== 0).length;
  const html = diffs
    .map(([type, text]) => {
      const chunk = markdownToHtmlSimple(text);
      if (type === 1) {
        return `<span style="background-color:${palette.addedBg};color:${palette.addedText};border-radius:4px;padding:1px 3px;">${chunk}</span>`;
      }
      if (type === -1) {
        return `<span style="background-color:${palette.removedBg};color:${palette.removedText};text-decoration:line-through;border-radius:4px;padding:1px 3px;">${chunk}</span>`;
      }
      return chunk;
    })
    .join("");
  return { html, changes };
};

export const formatDateTimeInputValue = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

export const formatShortDate = (dateString, withTime = false) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

export const formatRelative = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs)) return "";
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "ayer";
  if (days < 7) return `hace ${days} días`;
  return formatShortDate(dateString);
};

// ─── Thread comments ─────────────────────────────────────────────────────────

const stripHtml = (value) => String(value || "").replace(/<[^>]+>/g, "").trim();

/** Every message of the thread, system events included (`isEvent`), oldest first. */
export const getThreadComments = (messages) =>
  (Array.isArray(messages) ? messages : [])
    .filter(Boolean)
    .map((message) => ({
      ...message,
      isEvent: message.message_type === "event",
      text: stripHtml(message.content),
    }));

// ─── Piece workflow state ────────────────────────────────────────────────────

export const PUBLICATION_LABELS = {
  scheduled: "Programado",
  published: "Publicado",
};

/**
 * State of each of the three steps for a piece (Generación → Revisión →
 * Programación). Review covers the client's approval.
 */
export const getPieceWorkflow = (piece, { isClient = false } = {}) => {
  const status = piece?.status || "draft";
  const adaptations = piece?.platform_adaptations || [];
  const hasAdaptations = adaptations.length > 0 || piece?.has_platform_adaptations === true;
  const publication = piece?.publication_status || "none";
  const publishable = adaptations.filter((a) => isPublishablePlatform(a.platform)).length;
  const approved = status === "client_approved";
  const inReview = status === "client_review";

  const generation = hasAdaptations
    ? { state: "done", subtitle: `${adaptations.length || "Varias"} redes generadas` }
    : { state: "active", subtitle: piece?.raw_content?.trim() ? "Texto base listo" : "Pendiente de generar" };

  let review;
  if (approved) {
    review = { state: "done", subtitle: "Aprobado" };
  } else if (inReview) {
    review = { state: "active", subtitle: isClient ? "Esperando tu visto bueno" : "Esperando al cliente" };
  } else {
    review = { state: "blocked", subtitle: "Sin enviar" };
  }

  let scheduling;
  if (!approved) {
    scheduling = { state: "blocked", subtitle: "Tras la aprobación" };
  } else if (publication === "published") {
    scheduling = { state: "done", subtitle: "Publicado" };
  } else if (publication === "scheduled") {
    scheduling = {
      state: piece?.has_publication_errors ? "error" : "active",
      subtitle: piece?.has_publication_errors
        ? "Con errores"
        : `Programado ${formatShortDate(piece?.scheduled_at)}`.trim(),
    };
  } else {
    scheduling = {
      state: "active",
      subtitle: publishable
        ? "Sin programar"
        : piece?.scheduled_at
          ? `Publicación ${formatShortDate(piece.scheduled_at)}`.trim()
          : "Sin redes publicables",
    };
  }

  const steps = [
    { key: "generation", title: "Generación", ...generation },
    { key: "review", title: isClient ? "Tu revisión" : "Revisión del cliente", ...review },
    { key: "scheduling", title: "Programación", ...scheduling },
  ];

  let current = "generation";
  if (inReview) current = "review";
  else if (approved) current = "scheduling";

  // Once the piece is sent, generation is closed; review opens once sent, scheduling after approval.
  const lockedSteps = steps.map((step) =>
    (step.key === "generation" && current !== "generation") || step.state === "blocked"
      ? { ...step, disabled: true }
      : step
  );

  return { steps: lockedSteps, current };
};

const DISPLAY_STATUSES = CONTENT_STATUS_BADGES;

/** Status chip of a piece: `display_status` is the backend's single lifecycle state. */
export const getPieceDisplayStatus = (piece) =>
  DISPLAY_STATUSES[piece?.display_status] || DISPLAY_STATUSES[piece?.status] || DISPLAY_STATUSES.draft;

/**
 * Status chip of a group row: its approval status until its pieces go out.
 * Any piece with errors → "Con errores"; all published → "Publicado";
 * all scheduled or published → "Programado".
 */
export const getGroupDisplayStatus = (group) => {
  const statuses = (group?.pieces || []).map((piece) => piece.display_status);
  if (statuses.length) {
    if (statuses.includes("error")) return DISPLAY_STATUSES.error;
    if (statuses.every((status) => status === "published")) return DISPLAY_STATUSES.published;
    if (statuses.every((status) => status === "published" || status === "scheduled")) return DISPLAY_STATUSES.scheduled;
  }
  return DISPLAY_STATUSES[group?.status] || DISPLAY_STATUSES.draft;
};
